import * as THREE from 'three';
import type { StandardViewType, ViewOrientationTargetKind } from './standardViewTypes';

/**
 * 视图定向元素网格构造参数。
 */
export interface ViewOrientationElementMeshOptions {
  /** 标准视图类型。 */
  viewType: StandardViewType;
  /** 命中目标类型。 */
  kind: ViewOrientationTargetKind;
  /** 展示名称。 */
  label: string;
  /** 几何体。 */
  geometry: THREE.BufferGeometry;
  /** 材质。 */
  material: THREE.MeshBasicMaterial;
  /** 渲染顺序。 */
  renderOrder: number;
}

/**
 * 视图定向元素专用网格。
 *
 * 设计说明：
 * 1. 通过专用子类直接承载标准视图语义，禁止再把业务字段挂到 userData；
 * 2. 每个主面、边面、角面都对应一个独立实例，供拾取链直接读取强类型属性；
 * 3. 该类只负责语义承载，不参与几何生成策略。
 */
export class ViewOrientationElementMesh extends THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial> {
  /**
   * 视图定向器统一边长。
   *
   * 设计说明：
   * 所有子类（主面、边面、角点面、代理面）均基于此尺寸推导几何，
   * 集中定义确保整体比例一致，修改时只需改这一处。
   */
  public static readonly SIZE = 18;

  /**
   * 视图定向器统一渲染顺序基准值。
   *
   * 设计说明：
   * 子类（+0）、边线（+1）、hover 边线（+2）均基于此值偏移，
   * 保证渲染层次正确，代理面使用 RENDER_ORDER - 1。
   */
  public static readonly RENDER_ORDER = 30;

  /** 标准视图类型。 */
  public readonly viewType: StandardViewType;

  /** 命中目标类型。 */
  public readonly kind: ViewOrientationTargetKind;

  /** 展示名称。 */
  public readonly label: string;

  /**
   * 构造函数。
   *
   * @param options 构造参数。
   */
  public constructor(options: ViewOrientationElementMeshOptions) {
    super(options.geometry, options.material);
    this.viewType = options.viewType;
    this.kind = options.kind;
    this.label = options.label;
    this.name = `view-orientation-${options.viewType}`;
    this.renderOrder = options.renderOrder;
  }
}